// @generated by `./run-console lona-admin gen.ts` from
// tento/tento-lona-connector/src/row_type.rs — DO NOT EDIT.
//
// Canonical catalog of SheetRowDb.type strings. The Rust module is the
// authoritative taxonomy (roles, provider_system, contract status); this
// mirror lets the sheets package key its descriptor/spec registries off
// named consts and a parity test assert no cross-language drift.

import type { Role } from "../rows/descriptor.ts";
import type { Option } from "../core/option.ts";

/** One catalogued row type: its wire string, its role, and whether a provider system owns it. */
export interface RowTypeCatalogEntry {
  readonly ty: RowType;
  readonly role: Role;
  readonly providerSystem: boolean;
}

export const Layout = {
  COLUMN: "column",
  GRID: "grid",
  TIMELINE_ROW: "timeline[row]",
  TIMELINE_COLUMN: "timeline[column]",
  CANVAS: "canvas",
  CALENDAR_BODY: "calendar-body",
} as const;

export const Inert = {
  LABEL: "label",
  HEADER: "header",
  BLOCK: "block",
  GROUP: "group",
  SURFACE: "surface",
  SURFACE_CONTAINER: "surface-container",
  SHEET_FOOTER: "sheet-footer",
  TIMELINE_LEGEND: "timeline-legend",
  CALENDAR_LIST: "calendar-list",
} as const;

export const Alias = {
  ALIAS: "alias",
  CALENDAR: "alias.calendar",
  CALENDAR_LIST: "alias.calendar-list",
  CALENDAR_ALLDAY: "alias.calendar-allday",
  WEATHER: "alias.weather",
  TIMEZONE: "alias.timezone",
  TASK: "alias.task",
  TASK_LIST: "alias.task-list",
} as const;

export const Source = {
  TEXT: "text",
  NUMBER: "number",
  CHECKMARK: "checkmark",
  FORMULA: "formula",
  GRAPH: "graph",
  STICKER: "sticker",
  VIDEO: "video",
  DATA: "data",
  DATA_V1: "data.v1",
  DATA_SOURCE: "data-source",
  DATA_SERIES: "data-series",
  DATA_JSON: "data-json",
  DERIVED: "derived",
  LOGS: "logs",
  SYNC_LOGS: "sync_logs",
  TASKS: "tasks",
  SCHEDULING_LINK: "scheduling-link",
  CALENDAR_EVENTS: "calendar-events",
  EVENTS_FREE_BUSY: "events-free-busy",
  JOURNAL: "journal",
  DEBUG: "debug",
} as const;

export const Renderer = {
  COLUMN_TEXT: "column.text",
  COLUMN_NUMBER: "column.number",
  COLUMN_CHECKMARK: "column.checkmark",
  COLUMN_GRAPH: "column.graph",
  COLUMN_SERIES_SUMMARY: "column.series-summary",
  COLUMN_DATA_JSON: "column.data-json",
  COLUMN_HEATMAP: "column.heatmap",
  COLUMN_WEATHER: "column.weather",
  COLUMN_TIMEZONE: "column.timezone",
  COLUMN_LOGS: "column.logs",
  COLUMN_STICKER: "column.sticker",
  COLUMN_EVENT_LIST: "column.event-list",
  COLUMN_VIDEO: "column.video",
  COLUMN_HEADER_SECONDARY: "column.header-secondary",
  COLUMN_TIMELINE: "column.timeline",
  COLUMN_TASK: "column.task",
  TIMELINE_ROW_EVENT: "timeline[row].event",
  TIMELINE_ROW_TASK: "timeline[row].task",
  TIMELINE_ROW_SPAN: "timeline[row].span",
  TIMELINE_ROW_TEXT: "timeline[row].text",
  TIMELINE_ROW_NUMBER: "timeline[row].number",
  TIMELINE_COLUMN_EVENT: "timeline[column].event",
  TIMELINE_COLUMN_TASK: "timeline[column].task",
  TIMELINE_COLUMN_TIMELINE_LEGEND: "timeline[column].timeline-legend",
  TIMELINE_COLUMN_SPAN: "timeline[column].span",
  TIMELINE_COLUMN_TEXT: "timeline[column].text",
  TIMELINE_COLUMN_NUMBER: "timeline[column].number",
  TIMELINE_COLUMN_POINTS: "timeline[column].points",
  TIMELINE_COLUMN_REGRESSION_CURVE: "timeline[column].regression-curve",
  TIMELINE_COLUMN_REGRESSION_BAND: "timeline[column].regression-band",
  GRID_EVENT: "grid.event",
  GRID_TASK: "grid.task",
  GRID_TEXT: "grid.text",
  CANVAS_HRULE: "canvas.hrule",
  CANVAS_POINTS: "canvas.points",
  CANVAS_LINE: "canvas.line",
  CANVAS_BAR: "canvas.bar",
  CANVAS_REGRESSION_CURVE: "canvas.regression-curve",
  CANVAS_REGRESSION_BAND: "canvas.regression-band",
  CANVAS_REGRESSION_FILL: "canvas.regression-fill",
  CANVAS_CANDLESTICK: "canvas.candlestick",
  CANVAS_FUNNEL: "canvas.funnel",
} as const;

/** Every catalogued row type string. */
export type RowType =
  | (typeof Layout)[keyof typeof Layout]
  | (typeof Inert)[keyof typeof Inert]
  | (typeof Alias)[keyof typeof Alias]
  | (typeof Source)[keyof typeof Source]
  | (typeof Renderer)[keyof typeof Renderer];

export const ALL: readonly RowTypeCatalogEntry[] = Object.freeze([
  Object.freeze({ ty: Layout.COLUMN, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Layout.GRID, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Layout.TIMELINE_ROW, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Layout.TIMELINE_COLUMN, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Layout.CANVAS, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Layout.CALENDAR_BODY, role: "layout", providerSystem: false }),
  Object.freeze({ ty: Inert.LABEL, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.HEADER, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.BLOCK, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.GROUP, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.SURFACE, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.SURFACE_CONTAINER, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.SHEET_FOOTER, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.TIMELINE_LEGEND, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Inert.CALENDAR_LIST, role: "inert", providerSystem: false }),
  Object.freeze({ ty: Alias.ALIAS, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.CALENDAR, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.CALENDAR_LIST, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.CALENDAR_ALLDAY, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.WEATHER, role: "alias", providerSystem: true }),
  Object.freeze({ ty: Alias.TIMEZONE, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.TASK, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Alias.TASK_LIST, role: "alias", providerSystem: false }),
  Object.freeze({ ty: Source.TEXT, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.NUMBER, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.CHECKMARK, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.FORMULA, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.GRAPH, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.STICKER, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.VIDEO, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DATA, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DATA_V1, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DATA_SOURCE, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DATA_SERIES, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DATA_JSON, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DERIVED, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.LOGS, role: "source", providerSystem: true }),
  Object.freeze({ ty: Source.SYNC_LOGS, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.TASKS, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.SCHEDULING_LINK, role: "source", providerSystem: true }),
  Object.freeze({ ty: Source.CALENDAR_EVENTS, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.EVENTS_FREE_BUSY, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.JOURNAL, role: "source", providerSystem: false }),
  Object.freeze({ ty: Source.DEBUG, role: "source", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_TEXT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_NUMBER, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_CHECKMARK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_GRAPH, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_SERIES_SUMMARY, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_DATA_JSON, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_HEATMAP, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_WEATHER, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_TIMEZONE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_LOGS, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_STICKER, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_EVENT_LIST, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_VIDEO, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_HEADER_SECONDARY, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_TIMELINE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.COLUMN_TASK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_ROW_EVENT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_ROW_TASK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_ROW_SPAN, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_ROW_TEXT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_ROW_NUMBER, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_EVENT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_TASK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_TIMELINE_LEGEND, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_SPAN, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_TEXT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_NUMBER, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_POINTS, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_REGRESSION_CURVE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.TIMELINE_COLUMN_REGRESSION_BAND, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.GRID_EVENT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.GRID_TASK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.GRID_TEXT, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_HRULE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_POINTS, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_LINE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_BAR, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_REGRESSION_CURVE, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_REGRESSION_BAND, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_REGRESSION_FILL, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_CANDLESTICK, role: "renderer", providerSystem: false }),
  Object.freeze({ ty: Renderer.CANVAS_FUNNEL, role: "renderer", providerSystem: false }),
]);

/** Whether `ty` is a catalogued row type. */
export function contains(ty: string): ty is RowType {
  return ALL.some((e) => e.ty === ty);
}

/** The catalog entry of `ty`, or `null` when it is not catalogued. */
export function entry(ty: string): Option<RowTypeCatalogEntry> {
  return ALL.find((e) => e.ty === ty) ?? null;
}
